/**
 * Getting started — the screen a new account is met by, and the one it can always come back to.
 *
 * The complaint this answers is the oldest one on the list: somebody signs up, lands on a
 * dashboard of zeroes, and is told nothing about what the product is for or which of the twenty
 * screens in the rail has to happen first. So this page does three things in order — it says who
 * they are and what they have bought, it says what the product does for that particular purchase,
 * and it lays out the route from an empty register to a working one.
 *
 * **Everything on it is module-aware.** The greeting names the modules the organisation holds, the
 * explanation is written per module, and the steps come from `buildRoute` — which never produces a
 * section for a module the account does not have. A letting-only agency reads nothing about plots
 * on this page, and a developer reads nothing about tenancies.
 *
 * **Formal throughout, and explained by example rather than by chat.** The wording is the wording
 * an agency would use in its own correspondence. Where a step needs illustrating, the illustration
 * is behind the marker beside it — `InfoTip` — so the page reads as a route and not as a document
 * about itself.
 */

import { useSession } from "@/lib/session";
import { useActiveModule } from "@/lib/module-rail";
import { Card, PageHeader, Spinner } from "@/components/ui";
import { MODULE_INFO } from "@rentorng/core";
import { IconFlag } from "@/components/walkthrough-icons";
import { WalkthroughPanel, WalkthroughVisibility, useWalkthrough, whatItDoes } from "@/components/walkthrough";

/**
 * The first name, for the greeting.
 *
 * A greeting that uses somebody's full legal name reads like a letter from a utility
 * company. Falling back to the whole string when there is no space in it is deliberate — a
 * one-word name is a name, not a failure.
 */
function firstName(name: string | null | undefined): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "";
  return trimmed.split(/\s+/)[0];
}

export function GettingStartedPage() {
  const { loading, user, org, modules } = useSession();
  /*
    `/app/getting-started` belongs to neither module, so the page has to be told which one it is
    being read from. The route below it is the route for that module alone — see `buildRoute` — and
    the description has to agree with it: a page that explains the development book and then lists
    only letting steps reads as half of it having failed to load.
  */
  const active = useActiveModule(modules);
  const state = useWalkthrough();

  if (loading) return <Spinner />;

  const greeting = firstName(user?.name);
  /* What the *account* holds, which is a fact about the account rather than about the module being
     read. It names both because both were bought; it is a sentence, not a link, and it is the one
     place in the product that says so. */
  const held = modules.map((module) => MODULE_INFO[module].label);

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Getting started"
        subtitle={
          state.total > 0 ? (
            <span data-getting-started-progress>
              {state.done} of {state.total} steps complete
            </span>
          ) : undefined
        }
        description="An ordered route from an account with nothing in it to a register you can work from. Each step is marked complete on its own, from the records held — there is nothing here to tick."
      />

      {/*
        The welcome itself.

        It names the person and the organisation because the one thing a new owner needs to be
        told, on the screen after sign-up, is that the account is theirs and it worked. It names
        the modules because that is what they chose and what the rest of the product will and will
        not show them.
      */}
      <Card className="mb-5">
        <div className="flex flex-wrap items-start gap-4">
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-brand-600 text-white">
            <IconFlag size={22} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-ink-900" data-welcome-heading>
              {greeting ? `Welcome, ${greeting}.` : "Welcome."}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-ink-600" data-welcome-agency>
              <span className="font-medium text-ink-800">{org?.name ?? "Your organisation"}</span> is
              registered and ready to use. Your account includes {held.join(" and ")}.
            </p>

            {whatItDoes([active]).map((sentence) => (
              <p key={active} className="mt-3 text-sm leading-relaxed text-ink-600">
                {sentence}
              </p>
            ))}

            <p className="mt-3 text-sm leading-relaxed text-ink-500">
              Nothing below has to be done in one sitting, and nothing is lost by leaving the page.
              Your progress is read from the records themselves each time you return.
            </p>
          </div>
        </div>
      </Card>

      <WalkthroughPanel />

      <div className="mt-5">
        <Card>
          <WalkthroughVisibility />
        </Card>
      </div>
    </div>
  );
}
